import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Blanket from '@atlaskit/blanket';
import { AppSwitcher } from '@atlaskit/navigation-system/top-nav-items';

import { AtlassianSwitcherPrefetchTrigger, setSwitcherRenderStartMark } from '@atlassian/switcher';

import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { AppSwitcherContent } from './AppSwitcherContent.tsx';

export const AppSwitcherButton = (): React.JSX.Element => {
	const intl = useIntl();
	const routeDataRef = useRouteDataRef();
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isOpen, setIsOpen] = useState(false);

	const onClick = () => {
		if (!isOpen) {
			setSwitcherRenderStartMark();
		}
		setIsOpen(!isOpen);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'globalNavigation',
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'atlassianSwitcher',
				attributes: {
					selectedItemPageContext: routeDataRef.current?.routeName,
					navigationLayer: 'global',
					navigationContainer: 'top',
					navVersion: '4',
					navdexPointType: START_TOUCH,
				},
			},
		}).fire();
	};

	const onClose = () => {
		setIsOpen(false);
	};

	return (
		// TODO: Add `shouldRenderToParent={true}` to meet a11y requirements. See
		// https://a11y.atlassian.net/browse/JCATS-788
		// https://hello.jira.atlassian.cloud/browse/DISCO-4291
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			content={() => <AppSwitcherContent />}
			trigger={(triggerProps) => (
				<AtlassianSwitcherPrefetchTrigger cloudId={cloudId} product="confluence">
					<div {...triggerProps}>
						<AppSwitcher
							label={intl.formatMessage(i18n.switcher)}
							isSelected={isOpen}
							onClick={onClick}
							interactionName="confluence_app_switcher"
						/>
						<Blanket
							testId="app-switcher-button"
							isTinted={false}
							shouldAllowClickThrough={!isOpen}
							onBlanketClicked={onClose}
						/>
					</div>
				</AtlassianSwitcherPrefetchTrigger>
			)}
			placement="bottom-start"
			rootBoundary="document"
		/>
	);
};

const i18n = defineMessages({
	switcher: {
		id: 'app-navigation.top-navigation.app-switcher',
		defaultMessage: 'Switch sites or apps',
		description: 'Label for the top navigation app switcher button',
	},
});
